import React, { FC } from 'react';
import cn from 'classnames';
import { useLandingPageContext } from 'context/LandingPageContext';
import { Reference } from 'store/landing/types';
import MiniBanner from 'components/LandingPage/MiniBanner/MiniBanner';
import LPButton, { BUTTON_TYPE, LPButtonSize } from 'components/LandingPage/LPButton/LPButton';
import styles from './MessageCardGroupBanner.module.scss';

export interface MessageCardGroupBannerProps {
  heading?: string;
  title?: string;
  description: string;
  imageDesktop: string;
  imageMobile: string;
  bannerImagePosition: string;
  references: Reference[];
  layout: string | null;
  messageCardGroupTitle?: string;
  cardsCount: number;
}

const MessageCardGroupBanner: FC<MessageCardGroupBannerProps> = ({
  heading,
  description,
  imageDesktop,
  imageMobile,
  bannerImagePosition,
  references,
  layout,
  cardsCount,
}) => {
  const { theme, isDesktopDevice } = useLandingPageContext();

  const desktopFlexOrder = bannerImagePosition === 'right' ? 'row' : 'row-reverse';

  if (!layout || layout === 'stacked') {
    return (
      <div
        className={cn(styles.cardItemContainer, styles[`theme-${theme}`])}
        style={{
          width: isDesktopDevice ? `${100 / cardsCount}%` : '100%',
        }}
      >
        {references.map((reference) => {
          const { buttonItem } = reference;
          const { button } = buttonItem;
          const { label, referenceUrl, style, size } = button;

          return (
            <div className={styles.cardItem}>
              <MiniBanner
                bannerAltText={label}
                imageUrl={isDesktopDevice ? imageDesktop : imageMobile}
                referenceUrl={button.referenceUrl}
              />
              <h3 className={styles.heading}>{heading}</h3>
              <p className={styles.description}>{description}</p>

              <LPButton
                buttonColor={BUTTON_TYPE[style].textColor}
                buttonBackgroundColor={BUTTON_TYPE[style].backgroundColor}
                buttonBorderColor={BUTTON_TYPE[style].borderColor}
                referenceUrl={referenceUrl}
                size={LPButtonSize[size]}
              >
                {label}
              </LPButton>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={cn(styles.container, styles[`theme-${theme}`])}>
      <div className={styles.banner} style={{ flexDirection: isDesktopDevice ? desktopFlexOrder : 'column-reverse' }}>
        <div className={styles.textContent}>
          <div className={styles.textContentContainer}>
            <div>
              {heading && <h3 className={styles.heading} dangerouslySetInnerHTML={{ __html: heading }} />}
              <div className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />
              {references && references.length > 0 && (
                <div className={styles.buttonContainer}>
                  {references.map((reference, index) => {
                    const { buttonItem } = reference;
                    const { button } = buttonItem;
                    if (!button) return null;
                    const { label, referenceUrl, style, size } = button;
                    return (
                      <div
                        key={label + index.toString()}
                        style={{
                          flexBasis: isDesktopDevice ? `${100 / references.length}%` : '100%',
                        }}
                        className={styles.buttonItem}
                      >
                        <LPButton
                          buttonColor={BUTTON_TYPE[style].textColor}
                          buttonBackgroundColor={BUTTON_TYPE[style].backgroundColor}
                          buttonBorderColor={BUTTON_TYPE[style].borderColor}
                          referenceUrl={referenceUrl}
                          size={LPButtonSize[size]}
                          fontWeight="bold"
                        >
                          <span dangerouslySetInnerHTML={{ __html: label }} />
                        </LPButton>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.image}>
          <div
            className={styles.imageContent}
            style={{
              backgroundImage: `url(${isDesktopDevice ? imageDesktop : imageMobile})`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default MessageCardGroupBanner;
